import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Validation from '__components/Inputs/Validation';
import { OutlineInputControl } from '__components/Inputs/InputControl';
import { useSetupAssistant } from '@kizenapps/engine/react';
import { AssistantField, SelectOption } from '@kizenapps/engine';
import { ButtonRadio } from 'ts-components/ButtonToggle';
import styled from '@emotion/styled';

const Wrapper = styled.div`
  margin: 16px 0 5px;
`;

export const RadioBlock = ({
  field,
  disabled = false,
}: {
  field: AssistantField;
  disabled?: boolean;
}) => {
  const {
    state,
    setState,
    evaluateExpression,
    shouldHideField,
    getFieldErrorState,
    registerFieldResetter,
    afterFieldChange,
    disabledKeys,
  } = useSetupAssistant();

  const options = useMemo(() => field.options ?? [], [field.options]);

  const [disabledOptions, setDisabledOptions] = useState<
    Record<string, boolean>
  >({});
  const [optionsEvaluated, setOptionsEvaluated] = useState(false);
  const initialized = useRef(false);

  const storedValue = (state as any)[field.key]?.value as
    | SelectOption
    | undefined;

  const preselectedOption = useMemo(() => {
    if (field.default === undefined || field.default === null) {
      return undefined;
    }

    const defaultOption = options.find((opt) => opt.value === field.default);

    if (defaultOption && !disabledOptions[defaultOption.value]) {
      return defaultOption;
    }

    return options.find((opt) => !disabledOptions[opt.value]);
  }, [options, field.default, disabledOptions]);

  const value = storedValue ?? preselectedOption;

  const isDisabled = disabledKeys.includes(field.key) || disabled;

  const setRadioValue = useCallback(
    (option: SelectOption) => {
      setState((prevState: any) => ({
        ...prevState,
        [field.key]: { type: 'radio', value: option },
      }));

      afterFieldChange(field.key);
    },
    [field.key, setState, afterFieldChange]
  );

  const clearRadioValue = useCallback(() => {
    setState((prevState: any) => {
      if (!(field.key in prevState)) {
        return prevState;
      }

      const next = { ...prevState };

      delete next[field.key];

      return next;
    });

    afterFieldChange(field.key);
  }, [field.key, setState, afterFieldChange]);

  useEffect(() => {
    registerFieldResetter(field.key, clearRadioValue);
  }, [field.key, registerFieldResetter, clearRadioValue]);

  useEffect(() => {
    if (!optionsEvaluated || initialized.current) {
      return;
    }

    initialized.current = true;

    if (!storedValue && preselectedOption) {
      setRadioValue(preselectedOption);
    }
  }, [optionsEvaluated, storedValue, preselectedOption, setRadioValue]);

  useEffect(() => {
    if (field.when) {
      evaluateExpression(field.when, field.key);
    }
  }, [field.when, evaluateExpression, field.key]);

  const stateHash = useMemo(() => JSON.stringify(state), [state]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      options.map(async (opt) => {
        if (!opt.disabled) {
          return [opt.value, false] as const;
        }

        const result = await evaluateExpression(
          opt.disabled,
          `${field.key}__option__${opt.value}`
        );

        return [opt.value, Boolean(result)] as const;
      })
    ).then((results) => {
      if (!cancelled) {
        setDisabledOptions(Object.fromEntries(results));
        setOptionsEvaluated(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [field.key, options, evaluateExpression, stateHash]);

  // A disabled option is never saved: move to the preselected option, or clear when there
  // is none. Keyed on storedValue, not value: using value loops forever.
  useEffect(() => {
    if (!storedValue || !disabledOptions[storedValue.value]) {
      return;
    }

    if (preselectedOption) {
      setRadioValue(preselectedOption);
    } else {
      clearRadioValue();
    }
  }, [
    disabledOptions,
    storedValue,
    preselectedOption,
    setRadioValue,
    clearRadioValue,
  ]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  if (shouldHideField(field.key)) {
    return null;
  }

  const errorState = getFieldErrorState(field.key);

  return (
    <Wrapper ref={wrapperRef}>
      <OutlineInputControl
        label={field.label}
        labelInfo={field.tooltip}
        isShowAsterisk={field.required}
      >
        <ButtonRadio
          options={options.map((opt) => ({
            label: opt.label,
            value: opt.value,
            disabled: Boolean(disabledOptions[opt.value]),
          }))}
          value={value?.value ?? ''}
          onChange={(newValue) => {
            const opt = options.find((o) => o.value === newValue);

            if (opt) {
              setRadioValue(opt);
            }
          }}
          disabled={isDisabled}
          variant="outline"
          highlightBackground
          color="var(--color-button-secondary-default)"
          fontColor="var(--color-button-secondary-default)"
          selectedColor="var(--color-background-white)"
          hoverColor={false}
          height="28px"
          borderRadius="standard"
          textTransform="uppercase"
          bold
          optionHorizontalPadding={28}
          truncate
          centered
        />
      </OutlineInputControl>
      <Validation target={wrapperRef.current} {...errorState} inModal />
    </Wrapper>
  );
};
